import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { Minus, Plus } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { cn, getContrastTextColor, hexToHslaOrPassthrough } from '@/lib/utils'
import type { ChartIcicleChartConfig, ChartIcicleNode, ChartIcicleOrientation } from './types'
import {
  buildIcicleTooltipRows,
  DEFAULT_GAP,
  DEFAULT_MIN_LABEL_SIZE,
  DEFAULT_TILE_BREADTH,
  formatIcicleValue,
  getIcicleDisplayValue,
  getIcicleNodeSize,
  getVisibleChildren,
  nodeHasChildren,
  resolveIcicleFill,
} from './icicleModel'

export function ChartIcicleBody({
  nodes,
  chartConfig,
  collapsedIds,
  onToggleCollapsed,
  isClickSuppressed,
}: {
  nodes: ChartIcicleNode[]
  chartConfig: ChartIcicleChartConfig
  collapsedIds: ReadonlySet<string>
  onToggleCollapsed: (id: string) => void
  isClickSuppressed?: () => boolean
}) {
  const orientation = chartConfig.orientation ?? 'horizontal'
  const isHorizontal = orientation === 'horizontal'
  const gap = chartConfig.gap ?? DEFAULT_GAP
  const tileBreadth = chartConfig.tileBreadth ?? DEFAULT_TILE_BREADTH
  const minLabelSize = chartConfig.minLabelSize ?? DEFAULT_MIN_LABEL_SIZE

  return (
    <div
      className={cn('flex min-h-0 min-w-0', isHorizontal ? 'h-full w-max flex-col' : 'h-max w-full flex-row')}
      style={{ gap }}
    >
      {nodes.map((node) => (
        <IcicleBranch
          key={node.id}
          node={node}
          chartConfig={chartConfig}
          orientation={orientation}
          gap={gap}
          tileBreadth={tileBreadth}
          minLabelSize={minLabelSize}
          collapsedIds={collapsedIds}
          onToggleCollapsed={onToggleCollapsed}
          isClickSuppressed={isClickSuppressed}
        />
      ))}
    </div>
  )
}

function IcicleBranch({
  node,
  chartConfig,
  orientation,
  gap,
  tileBreadth,
  minLabelSize,
  collapsedIds,
  onToggleCollapsed,
  isClickSuppressed,
}: {
  node: ChartIcicleNode
  chartConfig: ChartIcicleChartConfig
  orientation: ChartIcicleOrientation
  gap: number
  tileBreadth: number
  minLabelSize: number
  collapsedIds: ReadonlySet<string>
  onToggleCollapsed: (id: string) => void
  isClickSuppressed?: () => boolean
}) {
  const isHorizontal = orientation === 'horizontal'
  const visibleChildren = getVisibleChildren(node, collapsedIds)
  const size = getIcicleNodeSize(node)

  return (
    <div
      className={cn('flex min-h-0 min-w-0', isHorizontal ? 'flex-row' : 'flex-col')}
      style={{ flexGrow: size, flexBasis: 0, flexShrink: 1, gap }}
    >
      <IcicleTile
        node={node}
        chartConfig={chartConfig}
        orientation={orientation}
        tileBreadth={tileBreadth}
        minLabelSize={minLabelSize}
        collapsed={collapsedIds.has(node.id)}
        onToggleCollapsed={onToggleCollapsed}
        isClickSuppressed={isClickSuppressed}
      />
      {visibleChildren.length > 0 && (
        <div
          className={cn('flex min-h-0 min-w-0', isHorizontal ? 'h-full flex-col' : 'w-full flex-row')}
          style={{ gap }}
        >
          {visibleChildren.map((child) => (
            <IcicleBranch
              key={child.id}
              node={child}
              chartConfig={chartConfig}
              orientation={orientation}
              gap={gap}
              tileBreadth={tileBreadth}
              minLabelSize={minLabelSize}
              collapsedIds={collapsedIds}
              onToggleCollapsed={onToggleCollapsed}
              isClickSuppressed={isClickSuppressed}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function IcicleTile({
  node,
  chartConfig,
  orientation,
  tileBreadth,
  minLabelSize,
  collapsed,
  onToggleCollapsed,
  isClickSuppressed,
}: {
  node: ChartIcicleNode
  chartConfig: ChartIcicleChartConfig
  orientation: ChartIcicleOrientation
  tileBreadth: number
  minLabelSize: number
  collapsed: boolean
  onToggleCollapsed: (id: string) => void
  isClickSuppressed?: () => boolean
}) {
  const tileRef = useRef<HTMLButtonElement | HTMLDivElement | null>(null)
  const [crossAxisSize, setCrossAxisSize] = useState(0)
  const isHorizontal = orientation === 'horizontal'
  const fill = resolveIcicleFill(node, chartConfig.config)
  const textColor = getContrastTextColor(fill)
  const formattedValue = formatIcicleValue(getIcicleDisplayValue(node), chartConfig.valueFormat)
  const tooltipRows = buildIcicleTooltipRows(node, chartConfig.config, formattedValue)
  const expandable = nodeHasChildren(node)
  const showLabels = chartConfig.showLabels !== false
  const showValues = chartConfig.showValues !== false
  const largeEnough = crossAxisSize >= minLabelSize

  useLayoutEffect(() => {
    const element = tileRef.current
    if (!element) {
      return
    }
    const applySize = (width: number, height: number) => {
      setCrossAxisSize(isHorizontal ? height : width)
    }
    applySize(element.offsetWidth, element.offsetHeight)
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) {
        return
      }
      applySize(entry.contentRect.width, entry.contentRect.height)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [isHorizontal])

  const tileStyle: CSSProperties = {
    width: isHorizontal ? tileBreadth : undefined,
    height: isHorizontal ? undefined : tileBreadth,
    flexShrink: 0,
    backgroundColor: hexToHslaOrPassthrough(fill, 0.2),
    borderColor: fill,
    borderWidth: 1,
    borderRadius: 5,
    borderStyle: 'solid',
    // color: textColor,
  }

  const content = (
    <>
      {showLabels && largeEnough && (
        <span className="absolute left-1.5 right-6 top-1 truncate text-[11px] leading-tight opacity-90">
          {node.name}
        </span>
      )}
      {showValues && largeEnough && (
        <span className="absolute inset-0 flex items-center justify-center text-2xl font-medium tabular-nums">
          {formattedValue}
        </span>
      )}
      {expandable && largeEnough && (
        <span className="absolute bottom-1 right-1 opacity-80" aria-hidden>
          {collapsed ? <Plus className="size-3" strokeWidth={2.5} /> : <Minus className="size-3" strokeWidth={2.5} />}
        </span>
      )}
    </>
  )

  const className = cn(
    'relative min-h-0 min-w-0 overflow-hidden border-0 p-0 text-left',
    isHorizontal ? 'h-full' : 'w-full',
    expandable && 'cursor-pointer'
  )

  const tile = expandable ? (
    <button
      ref={(element) => {
        tileRef.current = element
      }}
      type="button"
      className={className}
      style={tileStyle}
      aria-expanded={!collapsed}
      aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${node.name}`}
      onClick={() => {
        if (isClickSuppressed?.()) {
          return
        }
        onToggleCollapsed(node.id)
      }}
    >
      {content}
    </button>
  ) : (
    <div
      ref={(element) => {
        tileRef.current = element
      }}
      className={className}
      style={tileStyle}
    >
      {content}
    </div>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>{tile}</TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          {tooltipRows.map((entry) => (
            <div key={`${entry.label}-${entry.value}`} className="contents">
              <dt className="text-background/70">{entry.label}</dt>
              <dd className="font-medium">: {entry.value}</dd>
            </div>
          ))}
        </dl>
      </TooltipContent>
    </Tooltip>
  )
}
